import React from "react";
import { InlineMath } from "react-katex";

/**
 * Dwa podobne kwadraty K_1 (mniejszy) i K_2 (większy).
 *
 * - scaleK: skala długości (bok K2 = scaleK * bok K1 w rysunku)
 * - size: szerokość SVG; wysokość jest ~0.7 * size
 *
 * Kwadraty są ustawione tak, żeby:
 *  - zachować rzeczywistą skalę k:1,
 *  - mieć stały odstęp między krawędziami,
 *  - zawsze mieścić się w rysunku dla k z przedziału 2–7.
 */
export default function SquaresSimilarityDiagram({
  size = 280,
  scaleK = 5,
  smallLabel = "K_1",
  largeLabel = "K_2",
}) {
  const width = size;
  const height = size * 0.7;

  const k = Math.max(2, Math.min(7, scaleK || 2));

  const margin = width * 0.08;
  const gap = width * 0.08; // stały odstęp między krawędziami kwadratów

  // Ograniczenie od strony wysokości (żeby duży się nie ucinał)
  const maxLargeByHeight = height - 2 * margin;

  // Ograniczenie od strony szerokości:
  // margin + sSmall + gap + sLarge + margin <= width
  // sSmall = sLarge / k
  // => sLarge * (1 + 1/k) <= width - 2*margin - gap
  const maxLargeByWidth =
    (width - 2 * margin - gap) / (1 + 1 / k);

  const sLarge = Math.min(maxLargeByHeight, maxLargeByWidth);
  const sSmall = sLarge / k;

  // Poziome położenie: mały po lewej, duży po prawej, stały odstęp między krawędziami
  const xSmall = margin;
  const xLarge = xSmall + sSmall + gap;

  // W pionie oba centrowane.
  const ySmall = height / 2 - sSmall / 2;
  const yLarge = height / 2 - sLarge / 2;
  const smallLabelOutside = sSmall < 38;

  return (
    <svg
      width={width}
      height={height}
      viewBox={`0 0 ${width} ${height}`}
      style={{ display: "block", margin: "0 auto", maxWidth: "100%", height: "auto" }}
    >
      {/* mniejszy kwadrat */}
      <rect
        x={xSmall}
        y={ySmall}
        width={sSmall}
        height={sSmall}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />

      {/* większy kwadrat */}
      <rect
        x={xLarge}
        y={yLarge}
        width={sLarge}
        height={sLarge}
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
      />

      {/* Przy duzej skali podpis nie miesci sie wewnatrz malego kwadratu. */}
      <foreignObject
        x={smallLabelOutside ? xSmall + sSmall / 2 - 32 : xSmall}
        y={smallLabelOutside ? ySmall + sSmall + 6 : ySmall}
        width={smallLabelOutside ? 64 : sSmall}
        height={smallLabelOutside ? 28 : sSmall}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <InlineMath math={smallLabel} />
        </div>
      </foreignObject>

      {/* podpis K2 */}
      <foreignObject
        x={xLarge}
        y={yLarge}
        width={sLarge}
        height={sLarge}
      >
        <div
          style={{
            width: "100%",
            height: "100%",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          <InlineMath math={largeLabel} />
        </div>
      </foreignObject>
    </svg>
  );
}
